/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import ExcelJS from "exceljs";
import { capitalizeWords } from "../../assets/helper.js";

export const TYPE_OPTIONS = ["Donation", "Payment"];
export const FEE_COVERAGE_OPTIONS = ["Default", "Optional", "Required", "Disabled"];
export const STATUS_OPTIONS = ["Open", "Unlisted", "Closed"];
export const DESCRIPTION_FORMAT_OPTIONS = ["Plain", "Formatted"];

// Column names must match the backend parser exactly
// (checkplus-backend routes/common/@projects/bulk-upload.js validateRow).
// "Type" only applies to nonprofit (CheckPlus) orgs — business (Plug)
// projects are always payment type, so the column is omitted there.
// Title/Description "(Default)" columns are required; "(EN)"/"(ZH)"/"(CN)"
// are optional per-language overrides — a blank cell omits that language.
// "Description Format" applies to every Description cell in the row: "Plain"
// (the default, and what a blank cell means) imports them as text, while
// "Formatted" expects description code copied from the project editor and
// keeps its bold / headings / lists.
// "Image ID" is optional — numeric ID(s) of images already uploaded to
// the org's media gallery (Manage Images, in the Project Manager toolbar).
// Use a single id, or comma-separated ids in display order (e.g. "1, 2, 3"),
// up to 6 images. IDs are shown without a "#" prefix in Manage Images.
export const buildProjectHeaders = (includeType) => [
  "Title (Default)",
  "Title (EN)",
  "Title (ZH)",
  "Title (CN)",
  "Description (Default)",
  "Description (EN)",
  "Description (ZH)",
  "Description (CN)",
  "Description Format",
  "Target Amount",
  ...(includeType ? ["Type"] : []),
  "Fee Coverage",
  "Status",
  "Return URL",
  "Image ID",
];

const COLUMN_WIDTH_OVERRIDES = {
  "Title (Default)": 30,
  "Title (EN)": 30,
  "Title (ZH)": 30,
  "Title (CN)": 30,
  "Description (Default)": 50,
  "Description (EN)": 50,
  "Description (ZH)": 50,
  "Description (CN)": 50,
  "Description Format": 20,
  "Return URL": 30,
  "Image ID": 24,
};

const sanitizeSheetName = (name) => {
  const cleaned = String(name || "Template").replace(/[\\/*?[\]:]/g, " ").slice(0, 28);
  return cleaned || "Template";
};

const colLetter = (n) => {
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/**
 * Generate the project import .xlsx template as a Blob.
 */
export const generateProjectTemplate = async ({
  organizationObj,
  includeType = true,
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "CheckPlus";
  workbook.created = new Date();

  const orgName = organizationObj && organizationObj.organizationName;
  const sheetName = sanitizeSheetName(
    orgName ? `${capitalizeWords(orgName)} Projects` : "Project Template"
  );
  const sheet = workbook.addWorksheet(sheetName);

  const headers = buildProjectHeaders(includeType);

  sheet.columns = headers.map((h) => ({
    header: h,
    key: h,
    width: COLUMN_WIDTH_OVERRIDES[h] || 16,
  }));
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8F0FE" },
  };

  const addDropdown = (header, options, allowBlank) => {
    const col = headers.indexOf(header) + 1;
    if (col === 0) return;
    const range = `${colLetter(col)}2:${colLetter(col)}1001`;
    sheet.dataValidations.add(range, {
      type: "list",
      allowBlank,
      formulae: [`"${options.join(",")}"`],
      showErrorMessage: true,
      errorTitle: `Invalid ${header}`,
      error: "Select a value from the dropdown list.",
    });
  };

  addDropdown("Description Format", DESCRIPTION_FORMAT_OPTIONS, true);
  if (includeType) addDropdown("Type", TYPE_OPTIONS, true);
  addDropdown("Fee Coverage", FEE_COVERAGE_OPTIONS, true);
  addDropdown("Status", STATUS_OPTIONS, true);

  const targetAmountCol = headers.indexOf("Target Amount") + 1;
  sheet.getColumn(targetAmountCol).numFmt = "#,##0.00";

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadProjectTemplate = async (opts) => {
  const blob = await generateProjectTemplate(opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `project-import-template-${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
